import type { UntypedFunction } from '@mnd-frontend/ts';
import { Button, Icons, Symbols } from '@mnd-frontend/ui/publish/components';
import { withLineClamp } from '@mnd-frontend/ui/publish/utils';
import { GREY_100, RED_500, RED_700, SPACING_1, SPACING_2 } from '@mnd-frontend/ui/shared/tokens';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type Props = {
  fileName: string;
  disabled?: boolean;
  isDraft?: boolean;
  error?: string;
  onChange?: UntypedFunction;
  handleFileDelete: UntypedFunction;
};

const Wrapper = styled.div`
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: ${GREY_100};
`;

const FilePreviewWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: ${SPACING_1};

  svg {
    #budicon-headphones,
    #budicon-alert-sign,
    #budicon-file,
    #budicon-video-cam {
      .cls-1 {
        fill: ${RED_500};
      }
      .cls-2 {
        fill: ${RED_700};
      }
    }
  }
  p {
    margin: ${SPACING_2};
  }
`;

const Filename = styled.p`
  text-align: center;
  max-width: 100%;
  margin:0;
  ${withLineClamp(3)};
`;

const DeleteButtonWrapper = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
`;

const DocumentUploadPreview = ({
  fileName,
  disabled,
  isDraft,
  error,
  onChange,
  handleFileDelete,
}: Props) => {
  const { t } = useTranslation();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = useCallback(() => {
    setIsRemoving(true);
    handleFileDelete && handleFileDelete();
  }, [handleFileDelete]);

  return (
    <Wrapper>
      <FilePreviewWrapper>
        <Symbols.File />
        <Filename>{fileName}</Filename>
        <DeleteButtonWrapper>
          {!error && isDraft && (
            <Button
              type="button"
              onClick={handleRemove}
              aria-label={t('common.remove')}
              disabled={isRemoving || disabled}
              icon={<Icons.Delete />}
            />
          )}
          {!error && !isDraft && onChange && (
            <Button configuration="toned" type="button" onClick={onChange}>
              {t('components_file-upload_replace-document')}
            </Button>
          )}
        </DeleteButtonWrapper>
      </FilePreviewWrapper>
    </Wrapper>
  );
};

export default DocumentUploadPreview;
